import { Link } from 'react-router-dom';
import { ArrowRight, MessageSquare } from 'lucide-react';
import { GiGardeningShears, GiFeather } from 'react-icons/gi';
import { MdCleaningServices } from 'react-icons/md';
import { cn } from '@/lib/utils';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import type { ServiceCategory } from '@/types';

interface ServiceCardProps {
  id: ServiceCategory;
  title: string;
  description: string;
  href: string;
  className?: string;
}

const serviceIcons: Record<ServiceCategory, React.ReactNode> = {
  gardening: <GiGardeningShears className="h-10 w-10" aria-hidden="true" />,
  cleaning: <MdCleaningServices className="h-10 w-10" aria-hidden="true" />,
  taxidermy: <GiFeather className="h-10 w-10" aria-hidden="true" />,
};

const categoryLabels: Record<ServiceCategory, string> = {
  gardening: 'Outdoor',
  cleaning: 'Indoor',
  taxidermy: 'Specialty',
};

const TAXIDERMY_LOGO = '/taxidermy/logo.png';

/**
 * Service card component with improved styling
 */
export function ServiceCard({
  id,
  title,
  description,
  href,
  className,
}: ServiceCardProps) {
  const isTaxidermy = id === 'taxidermy';

  return (
    <Card className={cn('card-hover relative flex flex-col overflow-hidden', className)}>
      {/* Taxidermy Logo Background */}
      {isTaxidermy && (
        <div
          className="pointer-events-none absolute -right-8 -top-8 opacity-[0.04]"
          aria-hidden="true"
        >
          <img
            src={TAXIDERMY_LOGO}
            alt=""
            className="h-40 w-40 object-contain dark:invert"
          />
        </div>
      )}

      <CardHeader className="relative pb-4">
        {/* Icon container */}
        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 text-primary ring-1 ring-primary/10">
          {serviceIcons[id]}
        </div>
        {/* Category label */}
        <span className="category-label">{categoryLabels[id]}</span>
        <h3 className="text-xl font-bold text-foreground">{title}</h3>
      </CardHeader>

      <CardContent className="relative flex-1 space-y-4">
        <p className="text-muted-foreground">{description}</p>
      </CardContent>

      {/* Price Section - Integrated footer style */}
      <div className="border-t border-border bg-muted/30 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <MessageSquare className="h-4 w-4" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Pricing</p>
              <p className="text-sm font-semibold text-foreground">Upon Request</p>
            </div>
          </div>
          <Button asChild size="sm" variant="ghost" className="group gap-1">
            <Link to={href}>
              Details
              <ArrowRight
                className="h-3 w-3 transition-transform group-hover:translate-x-1"
                aria-hidden="true"
              />
            </Link>
          </Button>
        </div>
      </div>
    </Card>
  );
}
